/**
 * Wire combat feedback controls in the settings popover (next to audio).
 */

import {
  loadCombatFeedback,
  mergeCombatFeedback,
  saveCombatFeedback,
  type CombatFeedbackSettings,
  type DiscourseLevel,
  type MotionIntensity,
} from "../game/combatFeedback";

export type CombatFeedbackPanelHandle = {
  getSettings: () => CombatFeedbackSettings;
  /** Subscribe to changes (BoardUi / DiscoursePopups). */
  onChange: (fn: (s: CombatFeedbackSettings) => void) => () => void;
  syncFromStorage: () => void;
  dispose: () => void;
};

function el<T extends HTMLElement>(root: ParentNode, id: string): T | null {
  return root.querySelector(`#${id}`) as T | null;
}

export function bindCombatFeedbackPanel(
  root: ParentNode,
): CombatFeedbackPanelHandle {
  const motionSel = el<HTMLSelectElement>(root, "feedback-motion");
  const discourseSel = el<HTMLSelectElement>(root, "feedback-discourse");

  let settings = loadCombatFeedback();
  const listeners = new Set<(s: CombatFeedbackSettings) => void>();
  const domOff: Array<() => void> = [];

  function emit(): void {
    for (const fn of listeners) fn(settings);
  }

  function syncDom(): void {
    if (motionSel) motionSel.value = settings.motion;
    if (discourseSel) discourseSel.value = settings.discourse;
  }

  function apply(partial: Partial<CombatFeedbackSettings>): void {
    settings = mergeCombatFeedback(settings, partial);
    saveCombatFeedback(settings);
    syncDom();
    emit();
  }

  function on(
    node: HTMLElement | null,
    type: string,
    fn: (e: Event) => void,
  ): void {
    if (!node) return;
    node.addEventListener(type, fn);
    domOff.push(() => node.removeEventListener(type, fn));
  }

  on(motionSel, "change", () => {
    const v = motionSel!.value as MotionIntensity;
    if (v === "full" || v === "reduced" || v === "off") {
      apply({ motion: v });
    }
  });

  on(discourseSel, "change", () => {
    const v = discourseSel!.value as DiscourseLevel;
    if (v === "off" || v === "minimal" || v === "full") {
      apply({ discourse: v });
    }
  });

  syncDom();

  return {
    getSettings: () => settings,
    onChange(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    syncFromStorage() {
      settings = loadCombatFeedback();
      syncDom();
      emit();
    },
    dispose() {
      for (const off of domOff) off();
      listeners.clear();
    },
  };
}
